// ABOUTME: Pure helpers for restoring a conversation's messages from the
// backend transcript (#2277). The store's restoreMessages action delegates
// here, so the replace/prepend semantics — and the title derivation shared
// with addMessage — stay unit-testable outside zustand.

import type { AIChatMessage, AIConversation } from '@/stores/aiChatTypes'
import { buildWelcomeMessage } from '@/stores/aiChatWelcome'

/** Derive a conversation title from a message body (shared with addMessage). */
export function generateTitle(content: string): string {
  const maxLength = 50
  const cleaned = content.trim().replace(/\n/g, ' ')
  if (cleaned.length <= maxLength) return cleaned
  return `${cleaned.slice(0, maxLength)}...`
}

/**
 * A message this session holds that a server page may not have yet: a user
 * turn not yet stored, or stored after the page was read, and an assistant
 * bubble still waiting for (or already showing) its reply. Its server row,
 * once on the page, replaces it (`backend_message_id` is that row's id).
 */
function isLive(message: AIChatMessage, onPage: ReadonlySet<string>): boolean {
  const backendId = message.metadata?.backend_message_id
  if (backendId) return !onPage.has(backendId)
  if (message.role === 'user') return message.status !== undefined
  return message.isStreaming === true
}

/**
 * Replace (or, with `prepend`, extend backwards) a conversation's messages
 * with a server-restored page, keeping each message's own id and timestamp —
 * restore is the one path where "when it happened" must come from the server,
 * not the clock (#2277: every restored message used to render the page-load
 * time, collapsing an hours-long conversation into a single fake minute).
 *
 * Prepend is the "Load earlier messages" path: incoming messages are older
 * than everything loaded, already in chronological order, and deduped by id so
 * a double-tap can never repeat a window. Returns the conversation unchanged
 * (same reference) when there is nothing new to add.
 */
export function applyRestoredMessages(
  conversation: AIConversation,
  messages: AIChatMessage[],
  prepend: boolean
): AIConversation {
  if (prepend) {
    const existing = new Set(conversation.messages.map((m) => m.id))
    const fresh = messages.filter((m) => !existing.has(m.id))
    if (fresh.length === 0) return conversation
    return { ...conversation, messages: [...fresh, ...conversation.messages] }
  }

  // This session's turns the page doesn't hold yet stay after it: a send made
  // before the page was fetched, and the bubble its reply will fill.
  const onPage = new Set(messages.map((m) => m.id))
  const liveTail = conversation.messages.filter((m) => isLive(m, onPage))
  const page = messages.length > 0 ? messages : [buildWelcomeMessage(new Date().toISOString())]
  const restored = liveTail.length > 0 ? [...messages, ...liveTail] : page

  let title = conversation.title
  if (title === 'New Conversation') {
    const firstUser = messages.find((m) => m.role === 'user')
    if (firstUser) title = generateTitle(firstUser.content)
  }

  const lastTimestamp = messages[messages.length - 1]?.timestamp
  return {
    ...conversation,
    messages: restored,
    title,
    // Recency reflects when the conversation last moved, not when it was
    // reopened — a restore must never float an old conversation to the top.
    updatedAt: lastTimestamp ?? conversation.updatedAt,
  }
}
